import React, { PureComponent } from 'react';
import { Loader } from 'react-loaders';

import { batchRequest } from '../../../../../../api/ApiService';

import {
  Wrapper,
  PreTitle,
  Title,
  Text,
  ContentContainer,
  Assessments
} from '../../../components/ui';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from 'recharts';

import ErrorAlert from '../../../../../../components/alerts/error';
import SelectorWrapper from '../../../components/selector-wrapper';

import {
  selfAssessmentQuestions,
  relevantKnowledgeQuestions,
  handlingConflictsQuestions
} from '../../../../../../data/peer-feedback';

class SelfAssessment extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isRelevantKnowledgeChart: false,
      isHandlingConflictsChart: null,
      selfAssessmentAverage: 0,
      collaborationAverage: 0,
      showError: false,
      chartData: [],
      defaultChartData: [],
      selectedUserId: 0,
      loaded: false
    };
  }

  componentDidMount() {
    const { surveysStats } = this.props;

    let selfAssessmentStats = [];
    let collaborationStats = [];

    let isRelevantKnowledgeChart = false;
    let isHandlingConflictsChart = false;

    surveysStats.forEach(surveyStats => {
      if (surveyStats.question_category === 'Self assessment' && selfAssessmentQuestions.some(selfAssessmentQuestion => surveyStats.question_text.toLowerCase().includes(selfAssessmentQuestion))) {
        selfAssessmentStats.push(surveyStats);
      }
      if (surveyStats.question_category === 'Collaboration' && selfAssessmentQuestions.some(selfAssessmentQuestion => surveyStats.question_text.toLowerCase().includes(selfAssessmentQuestion))) {
        collaborationStats.push(surveyStats);
      }

      if (surveyStats.question_text.toLowerCase().includes('having relevant knowledge, skills, and abilities')) {
        isRelevantKnowledgeChart = true;
      }
      if (surveyStats.question_text.toLowerCase().includes('handling conflicts')) {
        isHandlingConflictsChart = true;
      }
    });

    const chartLength = selfAssessmentStats.length > collaborationStats.length ? selfAssessmentStats.length : collaborationStats.length;
    const selfAssessmentAverage = (selfAssessmentStats.reduce((total, next) => total + Number(next.class_median), 0) / chartLength).toFixed(2);
    const collaborationAverage = (collaborationStats.reduce((total, next) => total + Number(next.class_median), 0) / chartLength).toFixed(2);
    const showError = (isRelevantKnowledgeChart && isHandlingConflictsChart) || (!isRelevantKnowledgeChart && !isHandlingConflictsChart)
      ? true
      : false;

    this.setState({
      isRelevantKnowledgeChart,
      isHandlingConflictsChart,
    }, () => {
      const chartData = this.generateChartData();
      this.setState({
        selfAssessmentAverage,
        collaborationAverage,
        showError,
        chartData,
        defaultChartData: chartData,
        loaded: true
      });
    });
  }

  generateChartData = (selfAssessmentDataProp, teamsAssessmentDataProp) => {
    const { isRelevantKnowledgeChart, isHandlingConflictsChart } = this.state;

    let chartData = [];
    if (isRelevantKnowledgeChart && !isHandlingConflictsChart) {
      chartData = this.generateChartDataForSpecificQuestions(
        relevantKnowledgeQuestions,
        selfAssessmentDataProp,
        teamsAssessmentDataProp
      );
    } else if (isHandlingConflictsChart && !isRelevantKnowledgeChart) {
      chartData = this.generateChartDataForSpecificQuestions(
        handlingConflictsQuestions,
        selfAssessmentDataProp,
        teamsAssessmentDataProp
      );
    }

    return chartData;
  }

  generateChartDataForSpecificQuestions = (
    questionsArray,
    selfAssessmentDataProp,
    teamsAssessmentDataProp
  ) => {
    const { surveysStats } = this.props;

    const selfAssessmentData = selfAssessmentDataProp ? selfAssessmentDataProp : surveysStats;
    const teamsAssessmentData = teamsAssessmentDataProp ? teamsAssessmentDataProp : surveysStats;

    const teamsAssessmentQuestionPropName = teamsAssessmentDataProp ? 'question' : 'question_text';

    const selfAssessmentValuePropName = selfAssessmentDataProp ? 'answer_text' : 'class_median';
    const teamsAssessmentValuePropName = teamsAssessmentDataProp ? 'median' : 'class_median';
    let selfAssessmentTotal = 0;
    let teamsAssessmentTotal = 0;
    let chartData = [];
    chartData = questionsArray.map(handlingConflictsQuestion => {
      const selfAssessment = selfAssessmentData.find(item =>
        item.question_text.toLowerCase().includes(handlingConflictsQuestion.toLowerCase())
        && item.question_category === 'Self assessment'
      );

      const teamsAssessment = teamsAssessmentData.find(item => {
        const teamsAssessmentExtraCondition = teamsAssessmentDataProp ? true : item.question_category === 'Collaboration';
        return (
          item[teamsAssessmentQuestionPropName].toLowerCase().includes(handlingConflictsQuestion.toLowerCase())
          && teamsAssessmentExtraCondition
        );
      });

      const selfAssessmentValue = selfAssessment ? Number(selfAssessment[selfAssessmentValuePropName]).toFixed(2) : '0.00';
      const teamsAssessmentValue = teamsAssessment ? Number(teamsAssessment[teamsAssessmentValuePropName]).toFixed(2) : '0.00';

      selfAssessmentTotal += Number(selfAssessmentValue);
      teamsAssessmentTotal += Number(teamsAssessmentValue);
      return (
        {
          selfAssessment: selfAssessmentValue,
          teamsAssessment: teamsAssessmentValue,
          question: handlingConflictsQuestion
        }
      );
    });

    chartData.push({
      selfAssessment: (selfAssessmentTotal / chartData.length).toFixed(2),
      teamsAssessment: (teamsAssessmentTotal / chartData.length).toFixed(2),
      question: 'Overall'
    });
    return chartData;
  }

  get teamPlayersOptions() {
    const { gamesTeamplayers } = this.props;
    let options = gamesTeamplayers.map(gamesTeamplayer => (
      {
        id: gamesTeamplayer.user.id,
        name: `${gamesTeamplayer.user.first_name} ${gamesTeamplayer.user.last_name}`
      }
    ));
    const sortedOptions = [...options].sort((a, b) => a.name.localeCompare(b.name));

    sortedOptions.unshift({ id: 0, name: 'Overall' });
    return sortedOptions;
  }

  hideErrorAlert = () => {
    this.setState({
      showError: false
    });
  };


  customAxisTick = ({ x, y, textAnchor, payload }) => {
    const regex = /(?![^\n]{1,10}$)([^\n]{1,10})\s/g;
    const labelRows = payload.value.split(regex).filter(i => i !== '');

    const labelRowHeight = 28;
    const marginTop = 24;
    const labelsToDisplay = labelRows.map((string, index) => {
      const newRowShift = index * labelRowHeight;
      return <tspan key={index} y={y + newRowShift + marginTop} x={x}>{string}</tspan>;
    })
    return (
      <g className="recharts-layer recharts-cartesian-axis-tick">
        <text
          x={x}
          y={y}
          textAnchor={textAnchor}
          style={{
            fontWeight: 700,
            fontSize: 12,
            lineHeight: 28,
            fill: '#3A507B'
          }}
        >
          {labelsToDisplay}
        </text>
      </g>
    );
  }

  handleSelectChange = (value) => {
    const { currentFinishedSurvey } = this.props;
    const {
      defaultChartData
    } = this.state;

    if (value === 0) {
      this.setState({
        selectedUserId: value,
        chartData: defaultChartData,
        loaded: true
      });
    } else {
      this.setState({
        loaded: false,
        selectedUserId: value,
      });

      batchRequest([
        `${process.env.REACT_APP_API_URL}v2/forms/answers-list/?survey=${currentFinishedSurvey.id}&giver=${value}`,
        `${process.env.REACT_APP_API_URL}v2/forms/score/${value}/${currentFinishedSurvey.id}/`,
      ])
        .then(res => {
          const formsAnswersList = res[0].body.results;
          const formsScore = res[1].body;

          const chartData = this.generateChartData(formsAnswersList, formsScore.user_score_question);

          this.setState({
            loaded: true,
            chartData
          });
        })
        .catch(err => { });
    }
  }

  render() {
    const {
      selfAssessmentAverage,
      collaborationAverage,
      showError,
      chartData,
      selectedUserId,
      loaded
    } = this.state;

    const options = this.teamPlayersOptions;
    return (
      <Wrapper>
        {showError && (
          <ErrorAlert
            title="Error | Data conflict"
            message="No data to display due to conflicting information. Please reach out to customer support."
            onHide={this.hideErrorAlert}
          />
        )}
        <PreTitle>Individual</PreTitle>
        <Title>Self-Assessment</Title>
        <Text>Here’s a snapshot of students perception of self in relation to how their teammates perceives them.  On average, students rated their team skills <b>{selfAssessmentAverage}</b> out of 5 whereas their teammates gave them a <b>{collaborationAverage}</b> rating.</Text>
        <SelectorWrapper
          label={'View'}
          options={options}
          onSelectorChange={this.handleSelectChange}
          selectedOptionId={selectedUserId}
        />
        {
          !loaded &&
          <Loader type="ball-scale-ripple" active />
        }
        {
          !showError && loaded &&
          <ContentContainer>
            <Assessments.Legends>
              <Assessments.Legend>Self Assessment</Assessments.Legend>
              <Assessments.Legend>Team’s Assessment</Assessments.Legend>
            </Assessments.Legends>
            <Assessments.Marks>
              <Assessments.Mark>5 = Strongly Agree</Assessments.Mark>
              <Assessments.Mark>3 = Neither</Assessments.Mark>
              <Assessments.Mark>1 = Strongly Disagree</Assessments.Mark>
            </Assessments.Marks>
            <ResponsiveContainer width={672} height={375}>
              <LineChart
                data={chartData.slice()}
                margin={{ top: 10, bottom: 40, right: 50 }}
              >
                <CartesianGrid
                  stroke="rgba(0, 0, 0, 0.1)"
                  vertical={false}
                />
                <XAxis
                  dataKey="question"
                  axisLine={false}
                  tickLine={false}
                  tickCount={chartData.length}
                  interval={0}
                  tick={this.customAxisTick}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 5]}
                  ticks={[0, 1, 2, 3, 4, 5]}
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    lineHeight: 28,
                    fill: '#3A507B'
                  }}
                  dx={-22}
                />
                <Tooltip cursor={false} />
                <Line
                  type="monotone"
                  dataKey="selfAssessment"
                  name="Self Assessment"
                  stroke="#52C5CD"
                  strokeWidth={3}
                  dot={false}
                  activeDot={false}
                />
                <Line
                  type="monotone"
                  dataKey="teamsAssessment"
                  name="Team’s Assessment"
                  stroke="#667689"
                  strokeWidth={3}
                  dot={false}
                  activeDot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ContentContainer>
        }
      </Wrapper>
    );
  }

}

export default SelfAssessment;



// WEBPACK FOOTER //
// ./src/instructor/components/peer-feedback/pages/dynamics/individual/self-assessment.js